import React, { FC, Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLazyQuery, useMutation } from '@apollo/client';
import { Table, TableCell, TableHead, TableRow, TableBody, TableContainer, Paper } from '@mui/material';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import useFixMissingScroll from 'utils/use-fix-missing-scroll';
import { createDraftScannerCaseOrderForDentistTemplate, scannerCaseListTemplate } from 'apollo/templates/scanner-case';
import ScannerCaseItem from './scanner-case-item.component';
import { ScannerCase, ScannerCaseFilter } from 'models/scanner-case';
import { useNavigate } from 'react-router-dom';
import ScannerCaseDialog from 'components/scanner-cases/shared/scanner-case-dialog/scanner-case-dialog.component';

type ScannerCaseTableProps = {
    scannerCaseFilter: ScannerCaseFilter;
};
const ScannerCaseTable: FC<ScannerCaseTableProps> = ({ scannerCaseFilter }: ScannerCaseTableProps) => {
    const { t: translate } = useTranslation(['common', 'scan-cases', 'orders']);
    const [isOpen, setIsOpen] = useState(false);
    const [caseId, setCaseId] = useState<string>('');
    const [lastOffset, setLastOffset] = useState(0);
    const [scannerCases, setScannerCases] = useState<ScannerCase[]>([]);
    const { showBoundary } = useDenthubErrorBoundary();
    const navigate = useNavigate();

    const [getScannerCases, { fetchMore, refetch }] = useLazyQuery(scannerCaseListTemplate, {
        fetchPolicy: 'cache-and-network',
        pollInterval: 5 * 60 * 1000,
        notifyOnNetworkStatusChange: true,
        onCompleted: ({ scannerCases }) => {
            setScannerCases(scannerCases);
            setLastOffset(scannerCases.length);
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const [createDraftScannerCaseOrderForDentist] = useMutation(createDraftScannerCaseOrderForDentistTemplate, {
        onCompleted: ({ createDraftScannerCaseOrderForDentist: orderId }) => {
            navigate(`/orders/create-action/draft/${orderId}`);
        },
        onError(error) {
            showBoundary(error);
        },
    });

    useEffect(() => {
        setLastOffset(0);
        getScannerCases({
            variables: {
                limit: 20,
                offset: 0,
                filter: scannerCaseFilter,
            },
        });
    }, [scannerCaseFilter]);

    const handleFetchMore = () => {
        if (fetchMore) {
            fetchMore({
                variables: {
                    offset: lastOffset,
                },
                updateQuery: (previousResult, { fetchMoreResult }) => {
                    if (!fetchMoreResult) return previousResult;
                    const prevRes = previousResult?.scannerCases || [];
                    const moreRes = fetchMoreResult?.scannerCases || [];
                    return {
                        scannerCases: [...prevRes, ...moreRes],
                    };
                },
            });
        } else {
            getScannerCases({
                variables: {
                    limit: 20,
                    offset: 0,
                    filters: scannerCaseFilter,
                },
            });
        }
    };

    const onScannerCaseClick = (caseId: string) => {
        setCaseId(caseId);
        setIsOpen(true);
    };

    const onDialogClose = () => {
        setIsOpen(false);
    };

    const handleCreateOrderClick = (caseId: string) => {
        createDraftScannerCaseOrderForDentist({ variables: { caseId } });
    };

    // Fix for infinite scroll not working on some screen sizes.
    useFixMissingScroll(handleFetchMore, scannerCases?.length || 0);

    return (
        <Fragment>
            <Fragment>
                <TableContainer component={Paper}>
                    <InfiniteScroll
                        style={{ padding: 0 }}
                        dataLength={scannerCases ? scannerCases.length : 0}
                        next={handleFetchMore}
                        throttleTimeInMs={75}
                        hasMore={true}
                        loader={undefined}
                    >
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>{translate('common:DENTIST')}</TableCell>
                                    <TableCell>{translate('common:PATIENT')}</TableCell>
                                    <TableCell>{translate('common:LAST_UPDATED')}</TableCell>
                                    <TableCell>{translate('scan-cases:COMMENTS')}</TableCell>
                                    <TableCell>{translate('scan-cases:INFORMATION')}</TableCell>
                                    <TableCell></TableCell>
                                </TableRow>
                            </TableHead>
                            {scannerCases ? (
                                <TableBody>
                                    {scannerCases.map((scannerCase) => (
                                        <ScannerCaseItem
                                            key={scannerCase._id}
                                            scannerCase={scannerCase}
                                            onCaseClick={onScannerCaseClick}
                                            onCreateOrderFromCase={handleCreateOrderClick}
                                        />
                                    ))}
                                </TableBody>
                            ) : undefined}
                        </Table>
                    </InfiniteScroll>
                </TableContainer>
                <ScannerCaseDialog
                    open={isOpen}
                    caseId={caseId}
                    onDialogClose={onDialogClose}
                    onCreateOrderFromScanCase={handleCreateOrderClick}
                    displayCreateOrderButton
                />
            </Fragment>
        </Fragment>
    );
};

export default ScannerCaseTable;
